import { useMemo, useState, type MouseEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef, type GridRenderCellParams, type GridRowParams, DataGrid } from '@mui/x-data-grid';
import type { SpeedProfileWithUsocs } from '../../api/model';
import {
  useUISpeedProfilesDeleteSpeedProfile,
  useUISpeedProfilesListSpeedProfiles,
} from '../../api/speed-profiles/speed-profiles';
import { AddSpeedProfileForm } from './AddSpeedProfileForm';
import { EditSpeedProfileUsocsForm } from './EditSpeedProfileUsocsForm';

export const UsocPage = () => {
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuProfile, setMenuProfile] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data: speedProfilesData,
    error,
    isFetching,
    refetch,
    isLoading,
  } = useUISpeedProfilesListSpeedProfiles();

  const { mutate: deleteSpeedProfile, isPending: isDeleting } = useUISpeedProfilesDeleteSpeedProfile();

  const groupedRows = useMemo<SpeedProfileWithUsocs[]>(
    () =>
      [...(speedProfilesData?.data ?? [])]
        .sort((a, b) => a.groupname.localeCompare(b.groupname))
        .map((p) => ({
          ...p,
          usocs: [...p.usocs].sort((a, b) => a.localeCompare(b)),
        })),
    [speedProfilesData],
  );

  // Get current USOCs for selected profile
  const selectedUsocs = useMemo(
    () => groupedRows.find((r) => r.groupname === selectedProfile)?.usocs ?? [],
    [groupedRows, selectedProfile],
  );

  const handleMenuOpen = (event: MouseEvent<HTMLElement>, profileName: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuProfile(profileName);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuProfile(null);
  };

  const handleDeleteClick = () => {
    setProfileToDelete(menuProfile);
    setDeleteDialogOpen(true);
    setDeleteError(null);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (!profileToDelete) {
      return;
    }

    deleteSpeedProfile(
      { groupname: profileToDelete },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setProfileToDelete(null);
          void refetch();
        },
        onError: (err) => {
          setDeleteError(err.response?.data?.detail ?? 'Failed to delete speed profile. Please try again.');
        },
      },
    );
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProfileToDelete(null);
    setDeleteError(null);
  };

  const columns: Array<GridColDef<SpeedProfileWithUsocs>> = [
    {
      field: 'groupname',
      headerName: 'Speed Profile',
      width: 180,
    },
    {
      field: 'usocs',
      headerName: 'USOCs',
      flex: 1,
      minWidth: 300,
      sortable: false,
      filterable: true,
      valueGetter: (_value, row) => row.usocs.join(' '),
      renderCell: (params) =>
        params.row.usocs.length === 0 ? (
          <Typography color="text.secondary">-</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1 }}>
            {params.row.usocs.map((usoc) => (
              <Chip key={usoc} label={usoc} size="small" variant="outlined" />
            ))}
          </Box>
        ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<SpeedProfileWithUsocs>) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row.groupname)}
          aria-label="actions"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const handleRowClick = (params: GridRowParams<SpeedProfileWithUsocs>) => {
    setSelectedProfile(params.row.groupname);
  };

  const handleCloseDrawer = () => {
    setSelectedProfile(null);
  };

  const handleSaveSuccess = () => {
    setSelectedProfile(null);
    void refetch();
  };

  const handleAddSuccess = () => {
    setOpenAddDialog(false);
    void refetch();
  };

  return (
    <Box sx={{ maxWidth: 1200, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexShrink={0}>
        <Typography variant="h4" fontWeight="bold">
          USOC Mappings
        </Typography>
        <Button variant="contained" onClick={() => setOpenAddDialog(true)}>
          Add Speed Profile
        </Button>
      </Stack>

      {error && <Alert severity="error">Failed to load USOC mappings</Alert>}

      <DataGrid
        rows={groupedRows}
        columns={columns}
        getRowId={(row) => row.groupname}
        loading={isLoading || isFetching}
        onRowClick={handleRowClick}
        getRowHeight={() => 'auto'}
        showToolbar
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: 'groupname', sort: 'asc' }],
          },
        }}
        disableRowSelectionOnClick
        hideFooterPagination
        sx={{
          flex: 1,
          minHeight: 400,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            py: 1,
            '&:focus, &:focus-within': {
              outline: 'none',
            },
          },
        }}
      />

      <Drawer anchor="right" open={selectedProfile !== null} onClose={handleCloseDrawer}>
        {selectedProfile && (
          <EditSpeedProfileUsocsForm
            speedProfile={selectedProfile}
            currentUsocs={selectedUsocs}
            onSuccess={handleSaveSuccess}
            onClose={handleCloseDrawer}
          />
        )}
      </Drawer>

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <AddSpeedProfileForm onSuccess={handleAddSuccess} onClose={() => setOpenAddDialog(false)} />
      </Dialog>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{
              primary: {
                color: 'error',
              },
            }}
          />
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Speed Profile</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete the speed profile <strong>{profileToDelete}</strong>? This will
            also remove all associated USOC mappings.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
